import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Box, CircularProgress, Typography } from '@mui/material';
import { PdfToolbar, PdfSidebar, PdfAnnotatePanel, PdfCanvas, PdfPage, usePdfView } from '../../../components/PdfToolbar';
import './VoucherFormat1PrintPage.css';

// A5 Journal Voucher print page — same concept as the Job Card print page:
// an HTML sheet per page + a toolbar that triggers window.print() ("Save as PDF").
// Data comes from GET /api/get-voucher-pdf-data (master + jvdts + billwise + company).

// Rows per A5 sheet. Every sheet is padded with blank ruled rows up to this count so
// all pages keep the same table height; totals/words only appear on the last sheet.
const ROWS_PER_PAGE = 12;

const ONES = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
const TENS = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

const below100 = (n) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ''}`);
const below1000 = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    return `${h ? `${ONES[h]} HUNDRED` : ''}${h && r ? ' ' : ''}${r ? below100(r) : ''}`;
};
// Indian numbering (Crore / Lakh / Thousand)
const intToWords = (num) => {
    if (num === 0) return 'ZERO';
    const parts = [];
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const rest = num % 1000;
    if (crore) parts.push(`${below100(crore)} CRORE`);
    if (lakh) parts.push(`${below100(lakh)} LAKH`);
    if (thousand) parts.push(`${below100(thousand)} THOUSAND`);
    if (rest) parts.push(below1000(rest));
    return parts.join(' ');
};
const amountInWords = (value) => {
    const total = Math.round((Number(value) || 0) * 100);
    const rupees = Math.floor(total / 100);
    const paise = total % 100;
    let words = `RUPEES : ${intToWords(rupees)}`;
    if (paise) words += ` AND ${below100(paise)} PAISE`;
    return `${words} ONLY`;
};

const toNum = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);
const formatAmt = (v) => {
    const n = toNum(v);
    return n === 0 ? '' : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// "2026-10-31" -> "31-10-26" (string split, no Date parsing, so no timezone shift)
const formatDate = (value) => {
    if (!value) return '';
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}-${m[2]}-${m[1].slice(2)}` : String(value);
};

export default function VoucherFormat1PrintPage({ connectionString }) {
    const { receiptId } = useParams();
    const [searchParams] = useSearchParams();
    const companyId = searchParams.get('companyId');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        axios
            .get(`${connectionString}/api/get-voucher-pdf-data`, { params: { receiptId, companyId } })
            .then((res) => { if (!cancelled) setData(res.data); })
            .catch((err) => {
                console.error('Failed to load voucher for print:', err);
                if (!cancelled) setError('Failed to load voucher.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [receiptId, companyId, connectionString]);

    const { rows, totalDebit, totalCredit } = useMemo(() => {
        const lines = Array.isArray(data?.jvdts) ? data.jvdts : [];
        const mapped = lines.map((l) => {
            const debit = toNum(l.Debit);
            const credit = toNum(l.Credit);
            const trType = String(l.TrType || '').toLowerCase();
            const isDebit = trType ? trType === 'dr' : debit > 0;
            return {
                key: l.JVDetailsiD,
                prefix: isDebit ? 'To' : 'By',
                name: l.partyName || '',
                narration: l.Remark || '',
                debit,
                credit,
            };
        });
        return {
            rows: mapped,
            totalDebit: mapped.reduce((s, r) => s + r.debit, 0),
            totalCredit: mapped.reduce((s, r) => s + r.credit, 0),
        };
    }, [data]);

    const handlePrint = () => window.print();

    // Zoom / fit / page navigation / annotate / download state for the toolbar (hook must run before any early return).
    const view = usePdfView(Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE)));

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !data?.master) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <Typography color="error">{error || 'Voucher not found.'}</Typography>
            </Box>
        );
    }

    const { master, company } = data;
    const pages = [];
    for (let i = 0; i < Math.max(rows.length, 1); i += ROWS_PER_PAGE) {
        pages.push(rows.slice(i, i + ROWS_PER_PAGE));
    }
    const grandTotal = totalDebit || totalCredit;

    return (
        <>
            <PdfToolbar pageCount={pages.length} view={view} onPrint={handlePrint} downloadFilename={`Voucher-${master.ReceiptNumber || receiptId}.pdf`.replace(/[\\/:*?"<>|]/g, '-')} />
            <PdfSidebar view={view} />
            <PdfAnnotatePanel view={view} />

            <PdfCanvas view={view} className="vp-canvas">
                {pages.map((pageRows, pageIndex) => {
                    const isLast = pageIndex === pages.length - 1;
                    const blanks = Array.from({ length: ROWS_PER_PAGE - pageRows.length });
                    return (
                        <PdfPage view={view} index={pageIndex} className="vp-sheet" key={pageIndex}>
                            <div className="vp-header">
                                <div className="vp-company-name">{company?.CompanyName || ''}</div>
                                {company?.Address && <div className="vp-company-line">{company.Address}</div>}
                                {company?.MobileNo && <div className="vp-company-line">MO. : {company.MobileNo}</div>}
                                {company?.EmailId && <div className="vp-company-line">E-MAIL : {company.EmailId}</div>}
                            </div>

                            <div className="vp-title">JOURNAL VOUCHER</div>

                            <div className="vp-meta">
                                <div><span className="vp-meta-label">Voucher No :-</span> <span>{master.ReceiptNumber || ''}</span></div>
                                <div><span className="vp-meta-label">Date :-</span> <span>{formatDate(master.ReceiptDate)}</span></div>
                            </div>

                            <table className="vp-table">
                                <colgroup>
                                    <col className="vp-col-title" />
                                    <col className="vp-col-amt" />
                                    <col className="vp-col-amt" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Account Title &amp; Narration</th>
                                        <th>Debit Amt.</th>
                                        <th>Credit Amt.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.map((r) => (
                                        <tr key={r.key}>
                                            <td>
                                                <div className="vp-line"><span className="vp-prefix">{r.prefix}</span><span>{r.name}</span></div>
                                                {r.narration && <div className="vp-narration">{r.narration}</div>}
                                            </td>
                                            <td className="vp-amt">{formatAmt(r.debit)}</td>
                                            <td className="vp-amt">{formatAmt(r.credit)}</td>
                                        </tr>
                                    ))}
                                    {blanks.map((_, i) => (
                                        <tr key={`blank-${i}`} className="vp-blank-row">
                                            <td>&nbsp;</td>
                                            <td />
                                            <td />
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td className="vp-words">
                                            {isLast && master.Remark && <div className="vp-remark">{master.Remark}</div>}
                                            {isLast ? amountInWords(grandTotal) : ''}
                                        </td>
                                        <td className="vp-amt vp-total">{isLast ? formatAmt(totalDebit) : ''}</td>
                                        <td className="vp-amt vp-total">{isLast ? formatAmt(totalCredit) : ''}</td>
                                    </tr>
                                </tfoot>
                            </table>

                            <div className="vp-footer">
                                <div className="vp-for">For, {company?.CompanyName || ''}</div>

                                <div className="vp-signatures">
                                    <span>RECEIVER'S SIGNATURE</span>
                                    <span>Page {pageIndex + 1} of {pages.length}</span>
                                    <span>AUTHORISED SIGNATURE</span>
                                </div>
                            </div>
                        </PdfPage>
                    );
                })}
            </PdfCanvas>
        </>
    );
}
